/**
 * Unread, per thread: the one rule behind the feed's unread rows AND the channel / community
 * badges, on web and mobile alike.
 *
 * A thread is unread when someone else was active in it (the post itself, or a reply) after
 * BOTH the community's read floor and the last time you opened that thread
 * (`community_post_views`). Reading a post is what clears it; "Mark all read" moves the floor
 * to now. Opening a channel clears nothing.
 *
 * The badges used to count against per-channel marks (`communityLastSeen`) moved by opening a
 * channel, while the rows used the view records — so a post you had just read stayed counted
 * until you opened its channel, and opening a channel cleared threads you had never seen.
 *
 * Split in two so the badges follow your reading without another query:
 * `collectUnreadCandidates` walks the "what's new" hits once per load, and `summarizeUnread`
 * applies the floor and the view records to them — cheap enough to re-run every time a view
 * record lands or changes.
 */

/** The views that count replies to threads you wrote / saved. Not channels: no post is tagged with them. */
export const UNREAD_MY_POSTS_VIEW = "my_posts";
export const UNREAD_SAVED_POSTS_VIEW = "pinned_posts";

/** What a load found, before the floor and your view records are applied. */
export interface UnreadCandidates {
  /** Thread id → newest activity in it by someone else, epoch ms. */
  activity: Record<string, number>;
  /** Channel (or My Posts / Saved Posts view) id → the threads with such activity in it. */
  channels: Record<string, string[]>;
}

export interface UnreadSummary {
  /** Unread threads per channel or view; absent = none. */
  channels: Record<string, number>;
  /** Unread threads across the community's channels, each once — My / Saved Posts view the same threads, so add nothing. */
  total: number;
  /** The ids behind each count, so a caller can total several channels without counting a thread twice. */
  threads: Record<string, string[]>;
}

/** The fields of an Algolia post or reply record the walk reads. */
export interface UnreadHit {
  objectID?: string;
  parentID?: string;
  channels?: Array<string>;
  created?: any;
  deleted?: boolean;
  scheduled_date?: any;
}

export const EMPTY_UNREAD_SUMMARY: UnreadSummary = { channels: {}, total: 0, threads: {} };

/** Algolia carries Firestore timestamps as epoch numbers, some in seconds and some in milliseconds. */
export function unreadEpochMillis(value: any): number {
  const num = Number(value) || 0;
  if (!num) return 0;
  return num < 1e12 ? num * 1000 : num;
}

/**
 * Bucket the hits by thread. `hits` are someone else's posts and replies newer than the floor;
 * `channelIds` the channels (and views) the caller shows, so a thread only lands where it can
 * be seen. `myPostIds` / `savedPostIds` are the parent ids behind My Posts and Saved Posts: only
 * replies count there — a new post is either yours or not yet saved by you.
 *
 * Deleted items, and ones scheduled for later, are left out.
 */
export function collectUnreadCandidates(hits: Array<UnreadHit>, options: { channelIds: Array<string>; myPostIds?: Set<string>; savedPostIds?: Set<string>; now?: number }): UnreadCandidates {
  const allowed = new Set((options.channelIds || []).map((id) => (id ? String(id) : "")).filter(Boolean));
  const now = options.now ?? Date.now();
  const activity: Record<string, number> = {};
  const buckets: Record<string, Set<string>> = {};
  const add = (channelId: string, threadId: string) => {
    if (!allowed.has(channelId)) return;
    (buckets[channelId] = buckets[channelId] || new Set<string>()).add(threadId);
  };

  (hits || []).forEach((hit) => {
    if (!hit || hit.deleted) return;
    const scheduled = unreadEpochMillis(hit.scheduled_date);
    if (scheduled && scheduled > now) return;
    const created = unreadEpochMillis(hit.created);
    if (!created) return;
    const parentId = hit.parentID ? String(hit.parentID) : "";
    const threadId = parentId || (hit.objectID ? String(hit.objectID) : "");
    if (!threadId) return;

    if (created > (activity[threadId] || 0)) activity[threadId] = created;
    // Replies carry their parent's channels, so they bucket the same way posts do.
    (hit.channels || []).forEach((raw) => raw && add(String(raw), threadId));
    if (!parentId) return;
    if (options.myPostIds?.has(parentId)) add(UNREAD_MY_POSTS_VIEW, threadId);
    if (options.savedPostIds?.has(parentId)) add(UNREAD_SAVED_POSTS_VIEW, threadId);
  });

  const channels: Record<string, string[]> = {};
  Object.keys(buckets).forEach((id) => (channels[id] = Array.from(buckets[id])));
  return { activity, channels };
}

/**
 * The counts, now: each candidate thread against the floor and when you last opened it.
 * `viewedAt` returns 0 or undefined for never. A floor of 0 means it isn't known (signed out,
 * or the profile hasn't loaded) — that counts nothing, rather than everything.
 */
export function summarizeUnread(candidates: UnreadCandidates | null | undefined, floor: number, viewedAt: (threadId: string) => number | undefined): UnreadSummary {
  if (!candidates || !floor) return { channels: {}, total: 0, threads: {} };
  const verdicts: Record<string, boolean> = {};
  const isUnread = (threadId: string) => {
    if (verdicts[threadId] === undefined) {
      const at = candidates.activity[threadId] || 0;
      verdicts[threadId] = at > floor && at > (Number(viewedAt(threadId)) || 0);
    }
    return verdicts[threadId];
  };

  const channels: Record<string, number> = {};
  const threads: Record<string, string[]> = {};
  const inChannels = new Set<string>();
  Object.keys(candidates.channels).forEach((id) => {
    const unread = candidates.channels[id].filter(isUnread);
    if (!unread.length) return;
    channels[id] = unread.length;
    threads[id] = unread;
    if (id !== UNREAD_MY_POSTS_VIEW && id !== UNREAD_SAVED_POSTS_VIEW) unread.forEach((t) => inChannels.add(t));
  });
  return { channels, total: inChannels.size, threads };
}

/** Every thread a load found — the view records worth having before the counts are drawn. */
export function unreadCandidateThreadIds(candidates: UnreadCandidates | null | undefined): string[] {
  return candidates ? Object.keys(candidates.activity) : [];
}
